"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="20282" className="hidden lg:flex gap-8 items-center">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded-[2px]" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link to="/" className="group relative text-sm font-bold tracking-tight text-muted-foreground hover:text-foreground transition-colors py-2 font-sans">
              Home
              <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full shadow-[0_0_8px_rgba(var(--primary),0.5)]" />
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
        {menuItems.filter(item => item.href !== "/HomePage2" && item.href !== "/HomePage3").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-bold tracking-tight text-muted-foreground hover:text-foreground transition-colors py-2 font-sans">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full shadow-[0_0_8px_rgba(var(--primary),0.5)]" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-32 bg-muted/20 animate-pulse rounded-[2px]" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-6">
      <Link to="/" className="text-lg font-bold tracking-tight text-foreground/90 hover:text-primary transition-colors border-b border-border/20 pb-2 font-sans" onClick={onClose}>
        Home
      </Link>
      {menuItems.filter(item => item.href !== "/HomePage2" && item.href !== "/HomePage3").map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-bold tracking-tight text-foreground/90 hover:text-primary transition-colors border-b border-border/20 pb-2 font-sans" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="mt-4">
        <Button className="w-full bg-primary text-primary-foreground shadow-[0_0_15px_rgba(var(--primary),0.4)] hover:shadow-[0_0_25px_rgba(var(--primary),0.6)] transition-all duration-300 rounded-[2px] font-bold tracking-tighter uppercase" asChild onClick={onClose}>
            <Link to="#">Request Quote</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className="relative z-50 bg-background text-foreground">
      <header className={cn("sticky top-0 left-0 right-0 w-full transition-all duration-500 ease-in-out border-b z-50", isScrolled ? "bg-background/80 backdrop-blur-xl border-border/20 py-3 shadow-2xl" : "bg-background border-border/10 py-5")}>
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          
          <Link to="#" className="flex-shrink-0 z-50 block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621168/img/image_ns1j9s7s.jpg" alt="Sampson Concrete & Masonry Logo" className="h-7" />
          </Link>

          
          <MenuProvider menu_id="16">
            <DesktopNav />
          </MenuProvider>

          
          
          

          
          <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:text-primary hover:bg-transparent z-50" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        
        <div className={cn("fixed inset-0 bg-background/95 backdrop-blur-2xl z-40 lg:hidden transition-all duration-300 flex flex-col pt-24", isMobileMenuOpen ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-4")}>
          <MenuProvider menu_id="16">
            <MobileNav onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </header>
    </section>;
}